import Vec3 from '../vec3';
import { Booth, Camera, ExpositionHall, ExpositionHallDimension, Ground, LevelAssets, Orientation, StaticObject } from '../interfaces'

interface AsciiImage {
  url: string,
  thumbnail: string,
  orientation: Orientation
}

interface AsciiLink {
  url: string,
  description: string
}

interface AsciiCarpet {
  tileX: number,
  tileZ: number,
  sizeX: number,
  sizeZ: number,
  textureUrl: string
}

interface AsciiBlogPost {
  blogPostUrl: string,
  imageUrl: string,
  thumbnail: string,
  description: string
  orientation: Orientation
}

export interface AsciiExpositionHall {
    dimension: ExpositionHallDimension,
    sizeX: number,
    sizeZ: number,
    plan: string,
    banners: {
        [qualifier: string]: string
    },
    images: {
        [qualifier: string]: AsciiImage
    },
    videos: {
        [qualifier: string]: AsciiImage
    },
    blogPosts: {
      [qualifier: string]: AsciiBlogPost
    }
    links: {
        [qualifier: string]: AsciiLink
    },
    carpets: AsciiCarpet[]
}

function loadExpositionHall(asciiExpositionHall: AsciiExpositionHall, id: string, name: string): ExpositionHall {

  const { plan, sizeX, sizeZ, dimension, banners, images, videos, links, blogPosts } = asciiExpositionHall;

  // apparently the isometric camera ignores this (at least partly) ?!
  const camera: Camera = {
    name: 'camera0',
    position: new Vec3(0.5 * sizeX * dimension.tileSizeX, 50, 0.5 * sizeZ * dimension.tileSizeZ),
    lookingAt: new Vec3(0, 0, 0)
  }

  const generalBooth: Booth = {
    name: 'General Booth', // the whole exposition uses the same booth
    images: [],
    videos: [],
    links: [],
    blogPosts: []
  };

  const hall: ExpositionHall = {
    id,
    name,
    staticObjects: [],
    participantStarts: [],
    grounds: [],
    booths: [generalBooth],
    camera,
    dimension,
    carpets: []
  };

  // TODO: parse map
  const planStr = plan.split('\n').map(line => line.trim()).join('');

  for (let iz = 0; iz < sizeZ; iz++) {
    for (let ix = 0; ix < sizeX; ix++) {

      const position = new Vec3(ix * dimension.tileSizeX, 0, (sizeX - 1 - iz) * dimension.tileSizeZ);
      const tileSizeInDigits = 5;
      const mapTileType = planStr[tileSizeInDigits * (sizeX * iz + ix) + 1];
      const mapTileQualifierChar0 = planStr[tileSizeInDigits * (sizeX * iz + ix) + 2];
      const mapTileQualifierChar1 = planStr[tileSizeInDigits * (sizeX * iz + ix) + 3];
      const mapTileQualifier = `${ mapTileQualifierChar0 }${ mapTileQualifierChar1 }`
      // console.log(iz, ix, mapTileType, mapTileQualifier)
      let needGround = true;

      switch (mapTileType) {
        case '#': // Hole
          needGround = false;
          hall.staticObjects.push(createBlocker(position));
          break;

        case 'w': // Wall
          switch (mapTileQualifierChar0) {
            case '-':
              hall.staticObjects.push(createStaticObject(LevelAssets.WALL_PILLAR, position));
              break;
            case '|':
              hall.staticObjects.push(createStaticObject(LevelAssets.WALL_PILLAR, position));
              break;
            case '+':
            case 'r':
            case '7':
            case 'j':
            case 'l':
              hall.staticObjects.push(createStaticObject(LevelAssets.WALL_PILLAR, position));
              break;
          }
          break;

        case 's': // Start points
          let direction = new Vec3(1, 0, 0);
          switch (mapTileQualifier) {
            case 'u':
              direction = new Vec3(0, 0, 1);
              break;
            case 'd':
              direction = new Vec3(0, 0, -1);
              break;
            case 'l':
              direction = new Vec3(-1, 0, 0);
              break;
            case 'r':
              direction = new Vec3(1, 0, 0);
              break;
          }
          hall.participantStarts.push({
            position,
            direction
          })
          break;

        case 'b': // Banner
          const bannerIndex: number = parseInt(`0x${ mapTileQualifier }`);
          const bannerText = banners[bannerIndex];
          hall.staticObjects.push(createStaticObject(LevelAssets.BANNER, position, false, bannerText));
          break;

        case 'i': // Image
          const imageIndex: number = parseInt(`0x${ mapTileQualifier }`);
          const image = images[imageIndex];
          generalBooth.images.push({
            position,
            thumbnail: image.thumbnail,
            url: image.url,
            orientation: image.orientation
          })
          break;

        case 'x': // Image
          const postIndex: number = parseInt(`0x${ mapTileQualifier }`);
          const post = blogPosts[postIndex];
          generalBooth.blogPosts.push({
            position,
            thumbnail: post.thumbnail,
            blogPostUrl: post.blogPostUrl,
            imageUrl: post.imageUrl,
            orientation: post.orientation,
            description: post.description
          })
          break;

        case 'v': // Video
          const videoIndex: number = parseInt(`0x${ mapTileQualifier }`);
          const video = videos[videoIndex];
          generalBooth.videos.push({
            position,
            thumbnail: video.thumbnail,
            url: video.url,
            orientation: video.orientation
          })
          break;

        case 'l': // Links
          const linkIndex: number = parseInt(`0x${ mapTileQualifier }`);
          const link = links[linkIndex];
          generalBooth.links.push({
            position,
            url: link.url,
            description: link.description
          })
          break;

        case 't': // Table
          hall.staticObjects.push(createStaticObject(LevelAssets.TABLE, position));
          break;
        case 'c': // Cactus
          const asset = mapTileQualifier === '|' ? LevelAssets.CACTUS1 : LevelAssets.CACTUS2
          hall.staticObjects.push(createStaticObject(asset, position));
          break;

        case 'e': // Couch
          let orientation: Orientation;
          console.log("mapTileQualifier:",mapTileQualifier);
          console.log(mapTileQualifier === 'n ');
          switch (mapTileQualifier) {
            case 'n ':
              orientation = Orientation.NORTH;
              break;
            case 'e ':
              orientation = Orientation.EAST;
              break;
            case 's ':
              orientation = Orientation.SOUTH;
              break;
            case 'w ':
              orientation = Orientation.WEST;
              break;
            default:
              orientation = Orientation.WEST;
              break;
          }
          console.log("orientation:", orientation);
          hall.staticObjects.push(createStaticObject(LevelAssets.COUCH, position, true, null, orientation));
          break;
        case 'r': // Bookshelf
          hall.staticObjects.push(createStaticObject(LevelAssets.BOOKSHELF, position));
          break;
        case 'g': // Catering
          hall.staticObjects.push(createStaticObject(LevelAssets.CATERING, position));
          break;
        case 'p': // Poufs
          hall.staticObjects.push(createStaticObject(LevelAssets.POUFS, position));
          break;
        case 'd': // Plants
          hall.staticObjects.push(createStaticObject(LevelAssets.PLANTS, position));
          break;
        case 'j': //Tabletennis
          hall.staticObjects.push(createStaticObject(LevelAssets.TABLETENNIS, position))

      }

      if (needGround) {
        hall.grounds.push(createGround(position, dimension));
      }
    }
  }

  // add blockers around the whole exposition
  for (let iz = 0; iz < sizeZ; iz++) {
    hall.staticObjects.push(createBlocker(new Vec3(-1 * dimension.tileSizeX, 0, (sizeX - 1 - iz) * dimension.tileSizeZ)));
    hall.staticObjects.push(createBlocker(new Vec3(sizeX * dimension.tileSizeX, 0, (sizeX - 1 - iz) * dimension.tileSizeZ)));
  }

    // add carpets
  asciiExpositionHall.carpets.forEach((asciiCarpet: AsciiCarpet) => {
        const position = new Vec3(asciiCarpet.tileX * dimension.tileSizeX, 0 , (asciiExpositionHall.sizeX-1-asciiCarpet.tileZ) * dimension.tileSizeZ);
        const sizeX = asciiCarpet.sizeX * dimension.tileSizeX;
        const sizeZ = asciiCarpet.sizeZ * dimension.tileSizeZ;
        hall.carpets.push({
            position,
            sizeX,
            sizeZ,
            textureUrl: asciiCarpet.textureUrl
        })
    })

    return hall;
}

// to block player from walking through
function createBlocker(position: Vec3): StaticObject {
  const name = 'blocker-' + position.x + '-' + position.z;
  const staticObject: StaticObject = {
    name,
    assetType: LevelAssets.DEFAULT_ASSET, // doesn't matter since it is invisible anyway
    position,
    isBlocking: true,
    isVisible: false,
    payload: null
  }
  return staticObject;
}

function createStaticObject(assetType: number, position: Vec3, isBlocking: boolean = true, payload: string | undefined = undefined, orientation?: Orientation): StaticObject {
  const name = 'object-' + position.x + '-' + position.z;
  const staticObject: StaticObject = {
    name,
    assetType,
    position,
    isBlocking,
    isVisible: true,
    payload,
    orientation
  }
  return staticObject;
}

function createGround(position: Vec3, dimension: ExpositionHallDimension): Ground {
  const name = 'ground-' + position.x + '-' + position.z;
  const { tileSizeX, tileSizeZ } = dimension;
  const ground: Ground = {
    name,
    width: tileSizeX,
    height: tileSizeZ,
    position
  }
  return ground;
}


export { loadExpositionHall };
